import { createContext, useContext, useEffect } from "react";
import compose from "../helpers/compose";
import { makeProvider, normalize } from "../helpers/makeContexts";
import DashboardProvider, {
  useDashboardDispatch,
} from "./single-dashboard/DashboardContext";
import DashboardChartsProvider, {
  useDashboardChartsDispatch,
} from "./single-dashboard/DashboardChartsContext";
import { DashboardModalProvider } from "./single-dashboard/DashboardModalsContext";
import {
  DashboardDataTablesProvider,
  useDashboardDataTablesDispatch,
} from "./single-dashboard/DashboardDataTablesContext";
import DashboardCategoriesProvider, {
  useDashboardCategoriesDispatch,
} from "./single-dashboard/DashboardCategoriesContext";
import DashboardViewsFiltersProvider, {
  useDashboardViewsFiltersDispatch,
} from "./single-dashboard/DashboardViewsFiltersContext";
import useGetCharts from "../apiCalls/charts";
import DashChartTabsProvider from "./single-dashboard/DashChartTabsContext";
import DragAndDropProvider from "./single-dashboard/DragAndDropProvider";
import generateLocalFilters from "./single-dashboard/generateLocalFiltersHelper";
import { useAccountDataTableStore } from "./AccountDataTableContext";
import { useSampleDataTableStore } from "./SampleDataTableContext";
import convertDataTables from "../helpers/convertDataTables";

export const [
  DashboardSlotsGlobalProvider, //a global provider of all the slot contexts
  useUpdateLoaderData, //a hook to use in the LocalProvider to get the dispatch of that slot's data context (for saving) - see dataReducer
  DashboardSlotA,
] = createDashboardSlots(["A"]);

// slot context is for use within the local provider
const SlotContext = createContext();
// useSlotKey returns the key of the slot you are in when in the local provider (i.e. "A")
export function useSlotKey() {
  return useContext(SlotContext);
}

function createDashboardSlots(keys) {
  const allProviders = [];

  // the configuration for the AssignedDashboard context
  const assignedDashboardInit = {
    id: null,
    isLoading: true,
    isLoadingDashCharts: false,
    isLoadingDataTables: false,
    isLoadingCategories: false,
    isLoadingViewsFilters: false,
  };

  // the reducer for the AssignedDashboard dispatch
  const assignedDashboardReducer = (draft, { type, payload }) => {
    switch (type) {
      case "startLoading": {
        draft.isLoading = true;
        break;
      }
      case "setDashboardId": {
        draft.id = payload;
        break;
      }
      case "setDashboardIdAndStartLoading": {
        draft.id = payload;
        draft.isLoading = true;
        break;
      }
      case "stopLoading": {
        draft.isLoading = false;
        break;
      }
      case "startDashboardDataTablesLoading": {
        draft.isLoadingDataTables = true;
        break;
      }
      case "stopDashboardDataTablesLoading": {
        draft.isLoadingDataTables = false;
        break;
      }
      case "startDashboardChartsLoading": {
        draft.isLoadingDashCharts = true;
        break;
      }
      case "stopDashboardChartsLoading": {
        draft.isLoadingDashCharts = false;
        break;
      }
      case "startDashboardCategoriesLoading": {
        draft.isLoadingCategories = true;
        break;
      }
      case "stopDashboardCategoriesLoading": {
        draft.isLoadingCategories = false;
        break;
      }
      case "startDashboardViewsFiltersLoading": {
        draft.isLoadingViewsFilters = true;
        break;
      }
      case "stopDashboardViewsFiltersLoading": {
        draft.isLoadingViewsFilters = false;
        break;
      }
      default: {
        return null;
      }
    }
  };

  // the configuration for the Data context
  const dataInit = {
    dashboard: {},
    dataTables: {},
    categories: {},
    dashboardCharts: {},
    filters: {},
    views: [],
    activeFilters: {},
    activeViewId: null,
    cubeApi: null,
    isLoadingDashCharts: false,
  };

  // the reducer for the Data dispatch
  const dataReducer = (draft, { type, payload }) => {
    switch (type) {
      case "setAllDashboardData": {
        // add description
        let {
          id,
          title,
          description,
          Categories,
          DashboardUserSettings,
          DashboardDataTables,
          isTemplate,
          createdAt,
          updatedAt,
          AccountId,
          TemplateId,
          UserId,
          isPublishedToMarketplace,
          imgUrl,
          uniqueHash,
          embedUrl,
          AccountDashboardPermissionId,
          layout,
          trashDate,
          filterSettings,
          widgetGrouping,
          useDemoData,
        } = payload;

        draft.dashboard = {
          id,
          title,
          description,
          Categories,
          DashboardUserSettings,
          DashboardDataTables,
          isTemplate,
          createdAt,
          updatedAt,
          AccountId,
          TemplateId,
          UserId,
          isPublishedToMarketplace,
          imgUrl,
          uniqueHash,
          embedUrl,
          AccountDashboardPermissionId,
          layout: layout || { rows: [] },
          trashDate,
          filterSettings,
          widgetGrouping,
          useDemoData,
        };

        break;
      }
      case "setDashboardDataTablesData": {
        draft.dataTables = normalize(payload);
        break;
      }
      case "setDashboardChartsData": {
        draft.dashboardCharts = payload;
        break;
      }
      case "setDashboardCategoriesData": {
        draft.categories = normalize(payload, "CategoryId");
        break;
      }
      case "setIsLoadingDashCharts": {
        draft.isLoadingDashCharts = payload;
        break;
      }
      case "updateDashboard": {
        draft.dashboard = payload;
        break;
      }
      case "updateAccDashPermId": {
        draft.dashboard.AccountDashboardPermissionId = payload;
        break;
      }
      case "updateDUSinDashboard": {
        payload.forEach((dus) => {
          draft.dashboard.DashboardUserSettings[dus.id] = dus;
        });
        break;
      }
      case "updateDashCats": {
        draft.categories = normalize(payload);
        break;
      }
      case "updateDataTables": {
        draft.dataTables = payload;
        break;
      }
      case "addDataTable": {
        draft.dataTables[payload.id] = payload;
        break;
      }
      case "removeDataTable": {
        delete draft.dataTables[payload.id];
        break;
      }
      case "updateDashboardCharts": {
        draft.dashboardCharts = payload;
        break;
      }
      case "clearAllData": {
        draft.dashboard = {};
        draft.dataTables = {};
        draft.categories = {};
        draft.dashboardCharts = {};
        break;
      }
      case "setViewsAndFilters": {
        const { views, filters, activeFilters, activeViewId } = payload;
        draft.views = views;
        draft.filters = filters;
        draft.activeFilters = activeFilters;
        draft.activeViewId = activeViewId;
        break;
      }
      default: {
        return null;
      }
    }
  };

  // function to create a single DashboardSlot object
  function createDashboardSlot(key) {
    const AssignedDashboardContext = createContext();
    const AssignedDashboardDispatch = createContext();
    const DataContext = createContext();
    const DataDispatch = createContext();

    // a provider for all of this instance's global contexts
    const GlobalProvider = compose([
      makeProvider(
        AssignedDashboardContext,
        AssignedDashboardDispatch,
        assignedDashboardReducer,
        assignedDashboardInit
      ),
      makeProvider(DataContext, DataDispatch, dataReducer, dataInit),
    ]);

    allProviders.push(GlobalProvider);

    // a provider to the children components that will be rendering and editing dashboardA
    function LocalProvider({ children }) {
      const DashboardLocalProvider = compose([
        // Local providers needed for working with dashboards
        DashboardProvider,
        DashChartTabsProvider,
        DashboardChartsProvider,
        DashboardModalProvider,
        DashboardDataTablesProvider,
        DashboardCategoriesProvider,
        DragAndDropProvider,
        DashboardViewsFiltersProvider,
        ({ children }) => (
          <SlotContext.Provider value={key}>{children}</SlotContext.Provider>
        ),
      ]);

      return (
        <DashboardLocalProvider>
          <LocalLoader />
          {children}
        </DashboardLocalProvider>
      );
    }

    // uses the id assigned to the AssignedDashboardContext to load the all the local dashboard contexts with the data from the DataContext
    const LocalLoader = () => {
      const assignedDashboard = useContext(AssignedDashboardContext);
      const {
        dashboard,
        dataTables,
        categories,
        dashboardCharts,
        views,
        filters,
        activeFilters,
        activeViewId,
      } = useContext(DataContext);
      const { accountdatatables } = useAccountDataTableStore();
      const { sampledatatables } = useSampleDataTableStore();
      const dashboardEditDispatch = useDashboardDispatch();
      const dashChartsDispatch = useDashboardChartsDispatch();
      const dashboardDataDispatch = useDashboardDataTablesDispatch();
      const dashboardCatDispatch = useDashboardCategoriesDispatch();
      const dashboardViewsFiltersDispatch = useDashboardViewsFiltersDispatch();
      // const [isLoadingCubeMembers, setIsLoadingCubeMembers] = useState(true); // data is no longer loaded per dashboard from cube

      let allDashDTs = {}; // make a copy
      let index = 0;
      Object.values(dataTables).forEach((obj) => {
        allDashDTs[obj.id] = obj;
        index++;
      });
      let formatedData = convertDataTables(
        allDashDTs,
        accountdatatables,
        sampledatatables
      );

      useGetCharts();

      useEffect(() => {
        if (!assignedDashboard?.isLoading) {
          // take the data from DataContext and load it into the various Dashboard contexts
          dashboardEditDispatch({
            type: "setEditedDashboard",
            payload: dashboard,
          });
          dashboardDataDispatch({
            type: "setDataTables",
            payload: formatedData,
          });
          dashChartsDispatch({
            type: "setDashboardCharts",
            payload: dashboardCharts,
          });
          dashboardCatDispatch({
            type: "setDashCategories",
            payload: categories,
          });
          dashboardViewsFiltersDispatch({ type: "setViews", payload: views });
          dashboardViewsFiltersDispatch({
            type: "setFilters",
            payload: filters,
          });
          dashboardViewsFiltersDispatch({
            type: "setActiveFilters",
            payload: activeFilters,
          });
          dashboardViewsFiltersDispatch({
            type: "setActiveViewId",
            payload: activeViewId,
          });
          dashboardViewsFiltersDispatch({
            type: "setFiltersInActiveView",
            payload: activeFilters,
          });
          dashboardEditDispatch({
            type: "setWidgets",
            payload: dashboard?.widgetGrouping,
          });
          dashboardEditDispatch({
            type: "setWidgetGrouping",
            payload: dashboard?.widgetGrouping,
          });
          if (dashboard?.widgetGrouping?.length > 0) {
            const localFilterList = generateLocalFilters(
              dashboard?.widgetGrouping
            );
            // Dispatch the filters to context
            dashboardEditDispatch({
              type: "setLocalFilters",
              payload: localFilterList,
            });
          }
        }
      }, [
        assignedDashboard,
        dashboardEditDispatch,
        dashboardDataDispatch,
        dashboardCatDispatch,
        dashboardViewsFiltersDispatch,
        dashChartsDispatch,
        dashboard,
      ]);
    };

    // DashboardSlot object
    return {
      key, // the key string, i.e. "A" in DashboardSlotA

      // context hooks
      useAssignedDashboard: () => useContext(AssignedDashboardContext),
      useAssignedDashboardDispatch: () => useContext(AssignedDashboardDispatch),
      useDataContext: () => useContext(DataContext),
      useDataDispatch: () => useContext(DataDispatch),

      // Provider for wrapping local components that render/edit the dashboard
      LocalProvider,
    };
  }

  const allDashboardSlots = keys.map(createDashboardSlot);

  // a function to use whichever data dispatch goes with the LocalLoader
  function useUpdateLoaderData() {
    const slot = useSlotKey();
    // get the index of this slot
    const index = keys.indexOf(slot);
    const allDataDispatches = allDashboardSlots.map((s) => s.useDataDispatch());
    return allDataDispatches[index];
  }

  // [DashboardSlotsGlobalProvider, useUpdateLoaderData, DashboardSlotA, DashboardSlotB, DashboardSlotC]
  return [compose(allProviders), useUpdateLoaderData, ...allDashboardSlots];
}
